import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bug, ChevronLeft, X } from "lucide-react";
import { theme, btnPrimary, btnSecondary, inputStyle, floatingSheet, emptyFrame } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import {
  BUG_CATEGORY_LABELS, BUG_SEVERITY_LABELS, REPORTER_STATUS_LABELS, fmtWhen, recentBrowserErrors, statusColours,
  type BugCategory, type BugSeverity, type BugStatus,
} from "../lib/bugReports";
import { AttachmentGallery, ScreenshotPicker, releaseShots, uploadShots, type BugAttachment, type PendingShot } from "./BugReportScreenshots";

// Top bar → Report a problem. Every shell (client, consultant, auditor, and the
// Jersey and Essential Services editions, which mount the same AppShell) shows it;
// the onboarding wizards, which run before the shell, pin it top right (`floating`).
// Tickets go to the platform administrators (Platform Admin → Bug Reports);
// the reporter follows their own tickets here. An email link opens a ticket
// through ?bugReport=<id>.

interface MyReport {
  id: string;
  reference: string;
  title: string;
  description: string;
  stepsToReproduce: string | null;
  expectedResult: string | null;
  category: BugCategory;
  severity: BugSeverity;
  status: BugStatus;
  pageUrl: string | null;
  resolution: string | null;
  createdAt: string;
  lastActivityAt: string;
  _count?: { messages: number };
}

interface ThreadMessage {
  id: string;
  authorName: string;
  fromAdmin: boolean;
  body: string;
  statusChange: BugStatus | null;
  createdAt: string;
}

type View = { kind: "new" } | { kind: "list" } | { kind: "detail"; id: string } | { kind: "sent"; reference: string; id: string; warnings?: string[] };

const label: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 5 };
const hint: React.CSSProperties = { fontSize: 11.5, color: theme.color.subtle, marginTop: 4, lineHeight: 1.45 };

function StatusPill({ status }: { status: BugStatus }) {
  const c = statusColours(status);
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 3, color: c.color, background: c.bg, whiteSpace: "nowrap" }}>
      {REPORTER_STATUS_LABELS[status]}
    </span>
  );
}

export function BugReportButton({ appVersion, floating = false }: { appVersion?: string | null; floating?: boolean }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>({ kind: "new" });
  const [params, setParams] = useSearchParams();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // An email link: ?bugReport=<id> opens that ticket, then the parameter goes.
  useEffect(() => {
    const id = params.get("bugReport");
    if (!id) return;
    setView({ kind: "detail", id });
    setOpen(true);
    const next = new URLSearchParams(params);
    next.delete("bugReport");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const close = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => { setView({ kind: "new" }); setOpen(true); }}
        title="Report a problem"
        aria-label="Report a problem"
        aria-haspopup="dialog"
        style={floating
          ? { position: "fixed", top: 12, right: 12, zIndex: 900, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "6px 12px", borderRadius: 3, fontSize: 12, fontWeight: 600, fontFamily: "inherit", color: "var(--c-muted)", background: "var(--c-panel)", border: "1px solid var(--c-border)", boxShadow: "var(--c-sheetShadow)" }
          : { background: "none", border: "none", cursor: "pointer", padding: "4px 6px", display: "flex", alignItems: "center", borderRadius: 3, transition: "background 0.15s" }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--c-panelAlt)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = floating ? "var(--c-panel)" : "none")}
      >
        <Bug size={floating ? 15 : 18} style={{ color: "var(--c-muted)" }} />
        {floating && "Report a problem"}
      </button>
      {open && <BugReportDialog view={view} setView={setView} onClose={close} appVersion={appVersion ?? null} />}
    </>
  );
}

function BugReportDialog({ view, setView, onClose, appVersion }: { view: View; setView: (v: View) => void; onClose: () => void; appVersion: string | null }) {
  const titleId = useId();
  // Out of the way while the screen is captured, so the screenshot shows the page.
  const [hidden, setHidden] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onCloseRef.current(); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const heading = view.kind === "list" ? "My reports" : view.kind === "detail" ? "Report" : view.kind === "sent" ? "Report sent" : "Report a problem";

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, visibility: hidden ? "hidden" : "visible" }}>
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{
          ...floatingSheet,
          width: "100%", maxWidth: 620, maxHeight: "min(88vh, 820px)", display: "flex", flexDirection: "column",
          outline: "none", overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 18px", borderBottom: "1px solid var(--c-line)", flexShrink: 0 }}>
          {view.kind === "detail" && (
            <button type="button" onClick={() => setView({ kind: "list" })} aria-label="Back to my reports" style={iconBtn}>
              <ChevronLeft size={18} />
            </button>
          )}
          <Bug size={18} style={{ color: theme.color.accentText, flexShrink: 0 }} />
          <h2 id={titleId} style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.color.heading, flex: 1 }}>{heading}</h2>
          <div role="tablist" aria-label="Bug reports" style={{ display: "flex", gap: 4 }}>
            <TabButton active={view.kind === "new" || view.kind === "sent"} onClick={() => setView({ kind: "new" })}>New report</TabButton>
            <TabButton active={view.kind === "list" || view.kind === "detail"} onClick={() => setView({ kind: "list" })}>My reports</TabButton>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={iconBtn}><X size={18} /></button>
        </div>
        <div style={{ overflowY: "auto", padding: 18, flex: 1 }}>
          {view.kind === "new" && <NewReportForm appVersion={appVersion} onHideDialog={setHidden} onSent={(r) => setView({ kind: "sent", reference: r.reference, id: r.id, warnings: r.warnings })} />}
          {view.kind === "sent" && (
            <div style={{ textAlign: "center", padding: "24px 8px" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>Thank you - we have your report</div>
              <div style={{ fontSize: 13, color: theme.color.muted, lineHeight: 1.55, marginBottom: 18 }}>
                Your reference is <strong style={{ color: theme.color.heading }}>{view.reference}</strong>. The platform team will look at it and reply here; you will also get an email when we need more information or it is resolved.
              </div>
              {view.warnings && view.warnings.length > 0 && (
                <div role="alert" style={{ fontSize: 12.5, color: theme.color.warningText, background: theme.color.warningBg, borderRadius: 3, padding: "8px 10px", marginBottom: 14, textAlign: "left" }}>
                  Some screenshots did not upload - you can add them from the report: {view.warnings.join("; ")}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <button type="button" style={btnSecondary} onClick={() => setView({ kind: "detail", id: view.id })}>View report</button>
                <button type="button" style={btnPrimary} onClick={onClose}>Done</button>
              </div>
            </div>
          )}
          {view.kind === "list" && <MyReportsList onOpen={(id) => setView({ kind: "detail", id })} onNew={() => setView({ kind: "new" })} />}
          {view.kind === "detail" && <MyReportDetail id={view.id} onHideDialog={setHidden} />}
        </div>
      </div>
    </div>
  );
}

const iconBtn: React.CSSProperties = { background: "none", border: "none", cursor: "pointer", color: theme.color.muted, padding: 4, display: "flex", borderRadius: 3 };

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        fontSize: 12, fontWeight: active ? 600 : 500, padding: "5px 10px", borderRadius: 3, cursor: "pointer",
        border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
        background: active ? theme.color.accentBg : "transparent",
        color: active ? theme.color.accentText : theme.color.muted,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function NewReportForm({ appVersion, onSent, onHideDialog }: { appVersion: string | null; onSent: (r: { id: string; reference: string; warnings: string[] }) => void; onHideDialog: (hidden: boolean) => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [category, setCategory] = useState<BugCategory>("bug");
  const [severity, setSeverity] = useState<BugSeverity>("medium");
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [shots, setShots] = useState<PendingShot[]>([]);
  const [busy, setBusy] = useState(false);
  const shotsRef = useRef(shots);
  shotsRef.current = shots;
  useEffect(() => () => releaseShots(shotsRef.current), []);
  const [error, setError] = useState<string | null>(null);
  // Captured when the form opens: the page the problem was on, not whatever comes later.
  const [context] = useState(() => ({
    pageUrl: `${window.location.pathname}${window.location.search}`,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    errors: recentBrowserErrors(),
  }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (title.trim().length < 3) { setError("Give the report a short title."); return; }
    if (description.trim().length < 10) { setError("Describe what went wrong (a sentence or two is enough)."); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ report: { id: string; reference: string } }>("/bug-reports", {
        title, description, stepsToReproduce: steps, expectedResult: expected, category, severity,
        pageUrl: includeDiagnostics ? context.pageUrl : null,
        userAgent: includeDiagnostics ? context.userAgent : null,
        viewport: includeDiagnostics ? context.viewport : null,
        appVersion,
        diagnostics: includeDiagnostics && context.errors.length ? context.errors : null,
      });
      const warnings = await uploadShots(`/bug-reports/${encodeURIComponent(res.report.id)}/attachments`, shots);
      onSent({ ...res.report, warnings });
    } catch (err) {
      setError(apiErrorMessage(err, "The report could not be sent. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <p style={{ margin: "0 0 14px", fontSize: 12.5, color: theme.color.muted, lineHeight: 1.5 }}>
        Tell the platform team what went wrong. Please do not include passwords or personal data about other people.
      </p>
      <div style={{ marginBottom: 12 }}>
        <label style={label} htmlFor="bug-title">Summary</label>
        <input id="bug-title" style={inputStyle} value={title} maxLength={160} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Saving a new risk shows an error" autoFocus />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={label} htmlFor="bug-category">What kind of problem?</label>
          <select id="bug-category" style={inputStyle} value={category} onChange={(e) => setCategory(e.target.value as BugCategory)}>
            {(Object.keys(BUG_CATEGORY_LABELS) as BugCategory[]).map((c) => <option key={c} value={c}>{BUG_CATEGORY_LABELS[c]}</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor="bug-severity">How much does it affect you?</label>
          <select id="bug-severity" style={inputStyle} value={severity} onChange={(e) => setSeverity(e.target.value as BugSeverity)}>
            {(Object.keys(BUG_SEVERITY_LABELS) as BugSeverity[]).map((s) => <option key={s} value={s}>{BUG_SEVERITY_LABELS[s]}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={label} htmlFor="bug-description">What happened?</label>
        <textarea id="bug-description" style={{ ...inputStyle, minHeight: 90, resize: "vertical" }} value={description} maxLength={8000} onChange={(e) => setDescription(e.target.value)} placeholder="What you saw, including any error message" />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={label} htmlFor="bug-steps">Steps to reproduce <span style={{ fontWeight: 400, color: theme.color.subtle }}>(optional)</span></label>
        <textarea id="bug-steps" style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} value={steps} maxLength={8000} onChange={(e) => setSteps(e.target.value)} placeholder={"1. Open Risks\n2. Click Add risk\n3. ..."} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={label} htmlFor="bug-expected">What did you expect to happen? <span style={{ fontWeight: 400, color: theme.color.subtle }}>(optional)</span></label>
        <textarea id="bug-expected" style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} value={expected} maxLength={8000} onChange={(e) => setExpected(e.target.value)} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={label}>Screenshots <span style={{ fontWeight: 400, color: theme.color.subtle }}>(optional)</span></div>
        <ScreenshotPicker shots={shots} onChange={setShots} onHideDialog={onHideDialog} />
      </div>
      <div style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm, padding: "10px 12px", marginBottom: 14 }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, color: theme.color.text, cursor: "pointer" }}>
          <input type="checkbox" checked={includeDiagnostics} onChange={(e) => setIncludeDiagnostics(e.target.checked)} style={{ marginTop: 2 }} />
          <span>Include technical details to help us find the problem</span>
        </label>
        {includeDiagnostics && (
          <div style={{ ...hint, marginLeft: 24, wordBreak: "break-word" }}>
            Page: {context.pageUrl} · Browser window: {context.viewport}{appVersion ? ` · Version ${appVersion}` : ""}
            {context.errors.length > 0 && <> · {context.errors.length} recent browser error{context.errors.length === 1 ? "" : "s"}: {context.errors.slice(-2).map((e) => e.message).join("; ")}</>}
          </div>
        )}
      </div>
      {error && <div role="alert" style={{ color: theme.color.dangerText, fontSize: 12.5, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="submit" style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} disabled={busy}>{busy ? (shots.length ? "Sending report and screenshots..." : "Sending...") : "Send report"}</button>
      </div>
    </form>
  );
}

function MyReportsList({ onOpen, onNew }: { onOpen: (id: string) => void; onNew: () => void }) {
  const [reports, setReports] = useState<MyReport[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ reports: MyReport[] }>("/bug-reports").then((r) => setReports(r.reports)).catch((e) => setError(apiErrorMessage(e, "Your reports could not be loaded.")));
  }, []);

  if (error) return <div style={{ color: theme.color.dangerText, fontSize: 13 }}>{error}</div>;
  if (!reports) return <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading...</div>;
  if (reports.length === 0) {
    return (
      <div style={{ ...emptyFrame, padding: "24px 8px", fontSize: 13, color: theme.color.muted }}>
        You have not reported any problems.
        <div style={{ marginTop: 12 }}><button type="button" style={btnSecondary} onClick={onNew}>Report a problem</button></div>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {reports.map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => onOpen(r.id)}
          style={{ textAlign: "left", background: "transparent", border: `1px solid ${r.status === "awaiting_reporter" ? theme.color.danger : theme.color.border}`, borderRadius: theme.radius.sm, padding: "10px 12px", cursor: "pointer", font: "inherit", color: "inherit" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 11, fontVariantNumeric: "tabular-nums", color: theme.color.subtle }}>{r.reference}</span>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</span>
            <StatusPill status={r.status} />
          </div>
          <div style={{ fontSize: 11.5, color: theme.color.subtle }}>
            Raised {fmtWhen(r.createdAt)} · last update {fmtWhen(r.lastActivityAt)}{r._count?.messages ? ` · ${r._count.messages} message${r._count.messages === 1 ? "" : "s"}` : ""}
          </div>
        </button>
      ))}
    </div>
  );
}

function MyReportDetail({ id, onHideDialog }: { id: string; onHideDialog: (hidden: boolean) => void }) {
  const [report, setReport] = useState<MyReport | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [attachments, setAttachments] = useState<BugAttachment[]>([]);
  const [shots, setShots] = useState<PendingShot[]>([]);
  const shotsRef = useRef(shots);
  shotsRef.current = shots;
  useEffect(() => () => releaseShots(shotsRef.current), []);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<{ report: MyReport; messages: ThreadMessage[]; attachments: BugAttachment[] }>(`/bug-reports/${encodeURIComponent(id)}`)
      .then((r) => { setReport(r.report); setMessages(r.messages); setAttachments(r.attachments ?? []); setError(null); })
      .catch((e) => setError(apiErrorMessage(e, "This report could not be loaded.")));
  }, [id]);
  useEffect(load, [load]);

  async function send() {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const res = await api.post<{ messageId: string }>(`/bug-reports/${encodeURIComponent(id)}/messages`, { body: reply });
      setReply("");
      const failed = await uploadShots(`/bug-reports/${encodeURIComponent(id)}/attachments`, shots, res.messageId);
      releaseShots(shots);
      setShots([]);
      if (failed.length) setError(`Your reply was sent, but some screenshots were not: ${failed.join("; ")}`);
      load();
    } catch (e) {
      setError(apiErrorMessage(e, "Your reply could not be sent."));
    } finally {
      setBusy(false);
    }
  }

  async function closeReport() {
    if (!window.confirm("Close this report? Use this if the problem has gone away.")) return;
    setBusy(true);
    try {
      await api.post(`/bug-reports/${encodeURIComponent(id)}/close`);
      load();
    } catch (e) {
      setError(apiErrorMessage(e, "The report could not be closed."));
    } finally {
      setBusy(false);
    }
  }

  if (error && !report) return <div style={{ color: theme.color.dangerText, fontSize: 13 }}>{error}</div>;
  if (!report) return <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading...</div>;
  const closed = report.status === "closed";
  const galleryPath = `/bug-reports/${encodeURIComponent(id)}/attachments`;
  const onReport = attachments.filter((a) => !a.messageId);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", color: theme.color.subtle }}>{report.reference}</span>
        <StatusPill status={report.status} />
        <span style={{ fontSize: 11.5, color: theme.color.subtle }}>Raised {fmtWhen(report.createdAt)}</span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>{report.title}</div>
      <Section title="What happened">{report.description}</Section>
      {report.stepsToReproduce && <Section title="Steps to reproduce">{report.stepsToReproduce}</Section>}
      {report.expectedResult && <Section title="Expected">{report.expectedResult}</Section>}
      {report.resolution && (report.status === "resolved" || report.status === "closed") && <Section title="Resolution">{report.resolution}</Section>}
      {onReport.length > 0 && <Section title="Screenshots"><AttachmentGallery attachments={onReport} basePath={galleryPath} /></Section>}

      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.05em", margin: "16px 0 8px" }}>Conversation</div>
      {messages.length === 0 && <div style={{ fontSize: 12.5, color: theme.color.subtle, marginBottom: 10 }}>No replies yet. The platform team will reply here.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
        {messages.map((m) => (
          <ThreadItem key={m.id} m={m} labels={REPORTER_STATUS_LABELS} mine={!m.fromAdmin}>
            <AttachmentGallery attachments={attachments.filter((a) => a.messageId === m.id)} basePath={galleryPath} />
          </ThreadItem>
        ))}
      </div>

      {error && <div role="alert" style={{ color: theme.color.dangerText, fontSize: 12.5, marginBottom: 8 }}>{error}</div>}
      {closed ? (
        <div style={{ fontSize: 12.5, color: theme.color.subtle }}>This report is closed. If the problem comes back, please raise a new report.</div>
      ) : (
        <>
          <label style={label} htmlFor="bug-reply">{report.status === "awaiting_reporter" ? "The team needs more information - reply here" : report.status === "resolved" ? "Still happening? Reply to reopen" : "Add information"}</label>
          <textarea id="bug-reply" style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} value={reply} maxLength={8000} onChange={(e) => setReply(e.target.value)} />
          <div style={{ marginTop: 8 }}><ScreenshotPicker shots={shots} onChange={setShots} onHideDialog={onHideDialog} compact /></div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 8 }}>
            <button type="button" style={btnSecondary} onClick={closeReport} disabled={busy}>Close report</button>
            <button type="button" style={{ ...btnPrimary, opacity: busy || !reply.trim() ? 0.6 : 1 }} onClick={send} disabled={busy || !reply.trim()}>Send reply</button>
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 13, color: theme.color.text, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

/** One thread entry; shared with the admin queue (components/BugReportsAdminTab.tsx). */
export function ThreadItem({ m, labels, mine, internal = false, children }: { m: ThreadMessage; labels: Record<BugStatus, string>; mine: boolean; internal?: boolean; children?: React.ReactNode }) {
  if (!m.body && m.statusChange) {
    return (
      <div style={{ fontSize: 11.5, color: theme.color.subtle, textAlign: "center" }}>
        {m.authorName} marked this {labels[m.statusChange].toLowerCase()} · {fmtWhen(m.createdAt)}
      </div>
    );
  }
  return (
    <div
      style={{
        alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "88%",
        background: internal ? theme.color.warningBg : mine ? theme.color.accentBg : theme.color.panelAlt,
        border: `1px ${internal ? "dashed" : "solid"} ${internal ? theme.color.warningText : theme.color.border}`,
        borderRadius: theme.radius.sm, padding: "8px 11px",
      }}
    >
      <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 3 }}>
        <strong style={{ color: theme.color.heading }}>{m.authorName}</strong>{internal ? " · internal note" : ""} · {fmtWhen(m.createdAt)}
        {m.statusChange ? ` · marked ${labels[m.statusChange].toLowerCase()}` : ""}
      </div>
      <div style={{ fontSize: 13, color: theme.color.text, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}>{m.body}</div>
      {children}
    </div>
  );
}
